/* eslint-disable */
import React, { cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';

/**
 * OverflowTooltip
 *
 * A reusable wrapper that shows a MUI Tooltip only when the child element's
 * text content is overflowing (i.e. truncated with text-overflow: ellipsis).
 *
 * The component automatically injects the three CSS properties required for
 * overflow detection (`overflow: hidden`, `textOverflow: ellipsis`,
 * `whiteSpace: nowrap`) into the child's `sx` prop. You do NOT need to add
 * those styles manually on the child.
 *
 * Props:
 *  - title        {string}       - Tooltip label.
 *  - children     {ReactElement} - A single React element. Must accept a `ref`
 *                                  (any DOM element or React.forwardRef component).
 *  - TooltipProps {object}       - Extra props forwarded to the MUI <Tooltip>.
 *
 * Usage example:
 *
 *   <OverflowTooltip title={holidayName}>
 *     <Typography variant="body1">{holidayName}</Typography>
 *   </OverflowTooltip>
 */

const ellipsisSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    minWidth: 0
};

const OverflowTooltip = ({ title, children, TooltipProps = {} }) => {
    const textRef = useRef(null);
    const [isOverflowed, setIsOverflowed] = useState(false);

    const updateOverflow = useCallback(() => {
        const el = textRef.current;
        if (!el) return;
        setIsOverflowed(el.scrollWidth > el.clientWidth);
    }, []);

    // Re-check whenever `title` changes (e.g. new row data).
    useEffect(() => {
        updateOverflow();
    }, [title, updateOverflow]);

    // Observe element resize to keep the overflow state in sync.
    useEffect(() => {
        const el = textRef.current;
        if (!el) return undefined;

        if (typeof ResizeObserver === 'undefined') {
            // Fallback: plain window resize listener
            window.addEventListener('resize', updateOverflow);
            return () => window.removeEventListener('resize', updateOverflow);
        }

        const resizeObserver = new ResizeObserver(() => updateOverflow());
        resizeObserver.observe(el);
        return () => resizeObserver.disconnect();
    }, [updateOverflow]);

    const tooltipEnabled = Boolean(title) && isOverflowed;

    // Merge ellipsis styles into the child's sx prop, then forward our ref.
    const child = cloneElement(children, {
        ref: textRef,
        sx: { ...ellipsisSx, ...children.props.sx }
    });

    return (
        <Tooltip
            arrow
            title={title || ''}
            disableHoverListener={!tooltipEnabled}
            disableFocusListener={!tooltipEnabled}
            disableTouchListener={!tooltipEnabled}
            {...TooltipProps}
        >
            {child}
        </Tooltip>
    );
};

export default OverflowTooltip;
